import { useMemo } from "react";
import { useGetList } from "ra-core";
import { SalesPerformanceView } from "./SalesPerformanceView";
import type {
  CommercialDashboardTotals,
  SalespersonPerformance,
  SalesPerformanceMetrics,
} from "./salesPerformanceTypes";

/**
 * Production loader for Sales Performance.
 * Directly queries server-side SQL views:
 * - commercial_dashboard_totals
 * - sales_performance_summary
 * Never downloads full datasets or converts query errors into false zero metrics.
 */
export const SalesPerformanceProduction = () => {
  const {
    data: totalsList,
    isPending: isPendingTotals,
    error: errorTotals,
  } = useGetList<CommercialDashboardTotals>("commercial_dashboard_totals", {
    pagination: { page: 1, perPage: 1 },
  });

  const {
    data: salesList,
    isPending: isPendingSales,
    error: errorSales,
  } = useGetList<SalespersonPerformance>("sales_performance_summary", {
    pagination: { page: 1, perPage: 200 },
    sort: { field: "id", order: "ASC" },
  });

  const isPending = isPendingTotals || isPendingSales;

  // Error condition:
  // 1. Explicit error returned by totals query
  // 2. Explicit error returned by sales summary query
  // 3. Totals query returned an empty list (unexpected: view should always return 1 row)
  const hasError =
    Boolean(errorTotals) ||
    Boolean(errorSales) ||
    (!isPending && (!totalsList || totalsList.length === 0));

  const metrics = useMemo<SalesPerformanceMetrics | null>(() => {
    if (isPending || hasError || !totalsList || totalsList.length === 0) {
      return null;
    }

    const totals = totalsList[0];

    return {
      totals,
      salesBreakdown: salesList ?? [],
    };
  }, [isPending, hasError, totalsList, salesList]);

  return (
    <SalesPerformanceView
      metrics={metrics}
      isPending={isPending}
      error={hasError}
    />
  );
};
